// CP-8458: move a franchise parent's Main Office chat to the licensee that should answer it.
//
// Shown only to staff of an operator with licensees who hold chat.move_to_licensee, on Main Office chats.
// The backend (GET/POST chat/conversation_move/<unique_name>/) decides which licensees are offered: only those
// with staff who can answer chats, with a licensee preselected when the guest's phone or email matches one of
// its bookings. After a move the chat leaves this team's list, so the parent screen goes back to it; the
// confirmation's Undo carries a short-lived token from the move, because without it the chat is out of reach.
import * as React from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    IconButton,
    Radio,
    RadioGroup,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import DriveFileMoveOutlinedIcon from '@mui/icons-material/DriveFileMoveOutlined';
import { useSnackbar } from 'notistack';
import { CREATE, DataAPIContext, GET_ONE } from '../api';
import { ContextStore } from '../store/contextStore';
import { cv } from '../themeVars';

const RESOURCE = 'chat/conversation_move';

export default function MoveToLicenseeButton({ conversationId, isMainOffice, guestName, onMoved }) {
    const dataAPI = React.useContext(DataAPIContext);
    const { hasPerm, isFranchise } = React.useContext(ContextStore);
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();
    const [targets, setTargets] = React.useState(null);
    const [open, setOpen] = React.useState(false);
    const [selected, setSelected] = React.useState('');
    const [note, setNote] = React.useState('');
    const [busy, setBusy] = React.useState(false);
    const [error, setError] = React.useState('');

    const eligible = Boolean(conversationId && isMainOffice && isFranchise && hasPerm('chat', 'move_to_licensee'));

    React.useEffect(() => {
        setTargets(null);
        if (!eligible) return undefined;
        let live = true;
        dataAPI(GET_ONE, RESOURCE, { id: conversationId })
            .then((res) => {
                if (live && res?.statusCode === 200) setTargets(res.data);
            })
            .catch(() => {});
        return () => {
            live = false;
        };
    }, [dataAPI, conversationId, eligible]);

    if (!eligible || !targets || !(targets.licensees || []).length) return null;

    const openDialog = () => {
        setSelected(targets.suggested_id ? String(targets.suggested_id) : '');
        setNote('');
        setError('');
        setOpen(true);
    };

    const nameOf = (id) => (targets.licensees.find((t) => t.id === id) || {}).name || '';

    const undo = async (movedName, payload, snackKey) => {
        closeSnackbar(snackKey);
        const res = await dataAPI(CREATE, RESOURCE, {
            urlSuffix: `${encodeURIComponent(payload.twilio_conversation_unique_name)}/`,
            data: { licensee_id: payload.previous_tour_operator_id, undo_token: payload.undo_token },
        });
        if (res?.statusCode === 200) {
            enqueueSnackbar(`Moved back from ${movedName}`, { variant: 'success' });
        } else {
            enqueueSnackbar(res?.data?.message || "The chat couldn't be moved back. Ask the licensee team to move it.", { variant: 'error' });
        }
    };

    const move = async () => {
        if (!selected) {
            setError('Choose a licensee.');
            return;
        }
        setBusy(true);
        setError('');
        const res = await dataAPI(CREATE, RESOURCE, {
            urlSuffix: `${encodeURIComponent(conversationId)}/`,
            data: { licensee_id: Number(selected), note },
        });
        setBusy(false);
        if (res?.statusCode !== 200) {
            setError(res?.data?.message || "The chat couldn't be moved. Please try again.");
            return;
        }
        const movedName = res.data.tour_operator?.name || nameOf(Number(selected));
        setOpen(false);
        enqueueSnackbar(`Moved to ${movedName}`, {
            variant: 'success',
            autoHideDuration: 10000,
            action: (key) => (
                <Button color="inherit" size="small" onClick={() => undo(movedName, res.data, key)}>
                    Undo
                </Button>
            ),
        });
        if (onMoved) onMoved(res.data);
    };

    const selectedName = selected ? nameOf(Number(selected)) : 'the licensee';

    return (
        <>
            <Tooltip title="Move to licensee" arrow placement="top">
                <IconButton
                    aria-label="Move to licensee"
                    style={{ marginLeft: '-.3rem', width: '2.5rem', height: '2.5rem' }}
                    onClick={openDialog}
                >
                    {/* Same blue as the neighbouring header icons (their SVGs hardcode #007AFF);
                        unstyled, the MUI icon renders in the default gray and looks disabled. Keep the
                        default 24px: the folder then spans 20x16, like the Mark-unread envelope. */}
                    <DriveFileMoveOutlinedIcon style={{ color: cv('accent', '#007AFF') }} />
                </IconButton>
            </Tooltip>
            <Dialog open={open} onClose={() => !busy && setOpen(false)} maxWidth="xs" fullWidth aria-labelledby="move-to-licensee-title">
                <DialogTitle id="move-to-licensee-title">Move this chat to a licensee</DialogTitle>
                <DialogContent>
                    <Box display="flex" flexDirection="column" gap={2}>
                        <Typography variant="body2" color="text.secondary">
                            {guestName ? `${guestName} · ` : ''}
                            {targets.current?.name} Main Office
                        </Typography>
                        <RadioGroup aria-label="Move to" value={selected} onChange={(e) => setSelected(e.target.value)}>
                            {targets.licensees.map((t) => (
                                <FormControlLabel
                                    key={t.id}
                                    value={String(t.id)}
                                    control={<Radio size="small" />}
                                    label={
                                        <Box display="flex" alignItems="center" gap={1}>
                                            <span>{t.name}</span>
                                            {t.id === targets.suggested_id && (
                                                <Tooltip title="This guest's phone number or email matches one of this licensee's bookings">
                                                    <Chip size="small" color="success" variant="outlined" label="Suggested" />
                                                </Tooltip>
                                            )}
                                        </Box>
                                    }
                                />
                            ))}
                        </RadioGroup>
                        <TextField
                            id="move-to-licensee-note"
                            label="Note for the licensee team (optional)"
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            multiline
                            minRows={2}
                            inputProps={{ maxLength: 1000 }}
                            helperText="Saved as a staff note in the chat. The guest doesn't see it."
                        />
                        <Box component="ul" sx={{ m: 0, pl: 2.5, color: 'text.secondary', typography: 'body2' }}>
                            <li>The whole conversation moves, including earlier messages.</li>
                            <li>The guest's future messages go straight to {selectedName}.</li>
                            <li>The guest isn't notified.</li>
                            <li>Your team will no longer see this chat.</li>
                        </Box>
                        {error && <Alert severity="error">{error}</Alert>}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpen(false)} disabled={busy}>
                        Cancel
                    </Button>
                    <Button variant="contained" onClick={move} disabled={busy || !selected}>
                        {busy ? 'Moving…' : 'Move chat'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
